import i18n from "@/config/i18n";
import { getQueryParamValue } from "@/helpers/urlHelper";
import { dialogKey, DialogType } from "@/models/symbols/Dialog";
import { BookingState } from "@/models/www/store/BookingState";
import authenticateService from "@/services/authenticate.service";
import { storageService } from "@/services/storage.service";
import { useUserStore } from "@/store/user";
import { useEventBus } from "@vueuse/core";
import useRuntimeConfig from "./useRuntimeConfig";

const useAuth = () => {
	const dialogBus = useEventBus(dialogKey);
	const { getRuntimeConfig } = useRuntimeConfig();

	const authenticate = (id = ''): Promise<BookingState | undefined> => {
		return new Promise((resolve, reject) => {
			//Get runtimeconfig here so we don't have to later
			getRuntimeConfig().then(() => {
				if (!storageService.getToken() || id) {
					authenticateService.authenticate(id).then((resp) => {
						resolve(resp.booking);
					}).catch(() => {
						storageService.removeToken();
						reject();
					})
				} else {
					resolve(undefined);
				}
			})
		})
	}

	type PromiseFunction<T> = () => Promise<T>;
	const reAuthenticate = <T>(call: PromiseFunction<T>, count = 3): Promise<T> => {
		let lastError: Error;
		return new Promise((resolve, reject) => {
			if (count > 0) {
				authenticate().then(() => {
					call()
						.then((data: T) => {
							resolve(data)
						})
						.catch((err) => {
							if (err.response.status === 401) {
								storageService.removeToken();
								lastError = err;
								resolve(reAuthenticate(call, count - 1));
							} else {
								reject(err);
							}
						});
				});
			} else {
				reject(lastError ?? "");
			}
		});
	}

	const initializeRedirect = (force = false) => {
		const { getIsLoggedIn } = useUserStore();
		const route = getQueryParamValue("step") as string;

		if (force || getIsLoggedIn || ["3", "4", "5", "6", ""].includes(route)) {
			dialogBus.emit({
				title: i18n.global.t('messages.sessionExpired.title'),
				message: i18n.global.t('messages.sessionExpired.body'),
				yes: i18n.global.t('button.ok'),
				callback: () => {
					storageService.clearAll();
					window.location.replace(location.pathname);
				},
				type: DialogType.Redirect
			})
		}
	}

	return { authenticate, initializeRedirect, reAuthenticate }
}

export default useAuth;